import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { authService } from '../../../authService';
import CivilRegistryHome from './pages/CivilRegistryHome';
import ServicesManagement from './pages/ServicesManagement';
import OrderOfPayments from './pages/OrderOfPayments';
import MCRReports from './pages/MCRReports';
import MCRSettings from './pages/MCRSettings';
import MCRAccessLogin from './MCRAccessLogin';
import ProfileModal from '../../modals/ProfileModal';
import SettingsModal from '../../modals/SettingsModal';
import MobileBottomBar from '../../common/MobileBottomBar';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Assignment,
  Receipt,
  Assessment,
  Settings as SettingsIcon,
  ArrowBack,
  CardMembership,
  Logout,
  Person
} from '@mui/icons-material';

const drawerWidth = 280;

const MunicipalCivilRegistryDashboard = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [accessType, setAccessType] = useState(null); // 'login' or 'code'
  const [showAccessLogin, setShowAccessLogin] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Check for normal login first
    const currentUser = authService.getUserSession();
    
    if (currentUser) {
      // User is logged in normally
      setUser(currentUser);
      setAccessType('login');
      setShowAccessLogin(false);
    } else {
      // Check for access code session
      const accessSession = sessionStorage.getItem('mcr_access');
      
      if (accessSession) {
        try {
          const access = JSON.parse(accessSession);
          // Create a staff user object
          setUser({
            firstname: 'MCR',
            lastname: 'Staff',
            role: 'Registry Staff',
            accessCode: access.code
          });
          setAccessType('code');
          setShowAccessLogin(false);
        } catch (error) {
          // Invalid session, show access login
          setShowAccessLogin(true);
        }
      } else {
        // No authentication, show access code login
        setShowAccessLogin(true);
      }
    }
    document.title = 'Municipal Civil Registry - LGU Concepcion';
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/portal/civil-registry' || location.pathname === '/portal/civil-registry/') {
      // Redirect based on access type
      if (accessType === 'code') {
        navigate('/portal/civil-registry/orders', { replace: true });
      } else if (accessType === 'login') {
        navigate('/portal/civil-registry/home', { replace: true });
      }
    }
    
    // Prevent code users from accessing dashboard
    if (accessType === 'code' && location.pathname === '/portal/civil-registry/home') {
      navigate('/portal/civil-registry/orders', { replace: true });
    }
  }, [location.pathname, navigate, accessType]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    // Clear access code session if exists
    sessionStorage.removeItem('mcr_access');
    
    // Clear normal auth session
    authService.logout();
    
    if (onLogout) {
      onLogout();
    }
    
    // Code users should see access login again, normal users go to login
    if (accessType === 'code') {
      setUser(null);
      setAccessType(null);
      setShowAccessLogin(true);
    } else {
      navigate('/login', { replace: true });
    }
  };

  const handleBackToPortal = () => {
    if (accessType === 'login') {
      navigate('/portal');
    } else {
      // Code users can't go to portal, logout instead
      handleLogout();
    }
  };

  const handleAccessGranted = (accessUser) => {
    setUser(accessUser);
    setAccessType('code');
    setShowAccessLogin(false);
    navigate('/portal/civil-registry/orders');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    setShowSettingsModal(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    authService.setUserSession(updatedUser);
  };

  const menuItems = [
    { name: 'Dashboard', icon: <Dashboard />, path: '/portal/civil-registry/home' },
    { name: 'Services', icon: <Assignment />, path: '/portal/civil-registry/services' },
    { name: 'Order of Payments', icon: <Receipt />, path: '/portal/civil-registry/orders' },
    { name: 'Reports', icon: <Assessment />, path: '/portal/civil-registry/reports' },
    { name: 'Settings', icon: <SettingsIcon />, path: '/portal/civil-registry/settings' }
  ];

  // Filter menu items based on access type
  const filteredMenuItems = accessType === 'code' 
    ? menuItems.filter(item => item.name !== 'Dashboard')
    : menuItems;

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
      onOpenProfile={handleProfileClick}
      onOpenSettings={handleSettingsClick}
    />
  );

  // Show access code login if needed
  if (showAccessLogin) {
    return <MCRAccessLogin onAccessGranted={handleAccessGranted} />;
  }

  if (!user) return null;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Mobile Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          display: { md: 'none' },
          bgcolor: '#ffffff',
          color: '#0f172a',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, color: '#0f172a' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardMembership sx={{ color: '#059669', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
              Civil Registry
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawers */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          pb: { xs: 10, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#f8faf9',
          minHeight: '100vh',
          mt: { xs: 7, md: 0 }
        }}
      >
        <Routes>
          <Route index element={<CivilRegistryHome user={user} />} />
          <Route path="home" element={<CivilRegistryHome user={user} />} />
          <Route path="services" element={<ServicesManagement />} />
          <Route path="orders" element={<OrderOfPayments />} />
          <Route path="reports" element={<MCRReports />} />
          <Route path="settings" element={<MCRSettings user={user} />} />
        </Routes>
      </Box>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={filteredMenuItems}
        currentPath={location.pathname}
        onNavigate={(path) => navigate(path)}
        onToggleMenu={handleDrawerToggle}
        isMenuOpen={mobileOpen}
      />

      {/* Modals */}
      <ProfileModal
        user={user}
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onUpdate={handleProfileUpdate}
      />
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </Box>
  );
};

export default MunicipalCivilRegistryDashboard;
